import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, KanbanSquare, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";

export default function BoardsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [boards, setBoards] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [confirmBoard, setConfirmBoard] = useState(null);

  useEffect(() => {
    if (open) api.get("/board-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [open]);

  const load = () => api.get("/boards").then((r) => setBoards(r.data)).catch(() => setBoards([]));
  useEffect(() => { load(); }, []);

  const createBoard = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = templateId
        ? await api.post(`/board-templates/${templateId}/create-board`, { name, description: desc })
        : await api.post("/boards", { name, description: desc });
      setOpen(false); setName(""); setDesc(""); setTemplateId("");
      navigate(`/boards/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = (e, id) => {
    e.stopPropagation();
    setConfirmBoard(boards.find((b) => b.id === id) || null);
  };

  const doRemove = async () => {
    const id = confirmBoard.id;
    setConfirmBoard(null);
    try {
      await api.delete(`/boards/${id}`);
      setBoards((b) => b.filter((x) => x.id !== id));
      toast.success("Tablero eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="h-screen overflow-y-auto p-6 lg:p-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <KanbanSquare className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl font-extrabold">Tableros</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-board-button" className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
          <Plus className="h-4 w-4" /> Nuevo tablero
        </button>
      </div>

      {boards === null ? (
        <p className="text-zinc-500">Cargando…</p>
      ) : boards.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-2xl">
          <KanbanSquare className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-400 font-medium">Aún no tienes tableros</p>
          <p className="text-zinc-600 text-sm mb-6">Crea uno para organizar tu trabajo.</p>
          <button onClick={() => setOpen(true)} className="bg-primary hover:bg-[#FF5047] text-white font-semibold px-4 py-2.5 rounded-lg inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Crear tablero
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {boards.map((b, i) => {
            const pct = b.task_count ? Math.round((b.done_count / b.task_count) * 100) : 0;
            return (
              <div
                key={b.id}
                data-testid={`board-card-${b.id}`}
                onClick={() => navigate(`/boards/${b.id}`)}
                style={{ animationDelay: `${Math.min(i * 60, 540)}ms` }}
                className="group bg-[#121214] border border-white/10 rounded-xl p-5 cursor-pointer hover:border-primary/40 hover:-translate-y-1 transition-all animate-fade-up"
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                    <KanbanSquare className="h-5 w-5" />
                  </div>
                  {b.owner_id === user?.id && (
                    <button onClick={(e) => remove(e, b.id)} data-testid={`delete-board-${b.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-all">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <h3 className="font-display font-bold text-lg mt-4">{b.name}</h3>
                {b.client && <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#00B0FF]/15 text-[#00B0FF] mt-1" data-testid={`board-client-${b.id}`}>{b.client}</span>}
                <p className="text-sm text-zinc-500 line-clamp-2 min-h-[20px]">{b.description || "Sin descripción"}</p>
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
                    <span>{b.done_count}/{b.task_count} completadas</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-[#00E676] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 animate-fade-up" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">Nuevo tablero</h2>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-zinc-500 hover:text-white" /></button>
            </div>
            <form onSubmit={createBoard} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nombre</label>
                <input data-testid="board-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Marketing Q1" autoFocus
                  className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Descripción</label>
                <textarea data-testid="board-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="Opcional"
                  className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Empezar desde</label>
                <select data-testid="board-template-select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                  className="mt-1.5 w-full bg-[#0A0A0B] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-primary outline-none">
                  <option value="">Tablero en blanco</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.builtin ? " (plantilla)" : ""}</option>)}
                </select>
              </div>
              <button data-testid="board-create-submit" type="submit" disabled={saving} className="w-full bg-primary hover:bg-[#FF5047] text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Crear tablero
              </button>
            </form>
          </div>
        </div>
      )}
      {confirmBoard && (
        <ConfirmDialog
          title="Eliminar tablero"
          message={`¿Eliminar «${confirmBoard.name}» y todas sus tareas? Podrás restaurarlo desde la papelera de Administración.`}
          confirmLabel="Eliminar tablero"
          onConfirm={doRemove}
          onCancel={() => setConfirmBoard(null)}
        />
      )}
    </div>
  );
}
